<template>
  <div class="modal fade" :id="''+id" data-bs-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog modal-lg" role="document">
      <form @submit.prevent="submitForm">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ forUser ? $t('access.configAccess.addUsers') : $t('access.configAccess.addGroups') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal" aria-label="Close">
              <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                        fill="currentColor"/>
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                        fill="currentColor"/>
                  </svg>
              </span>
            </div>
          </div>
          <div class="modal-body scroll-y" style="max-height: 75vh;">
            <div class="row">
              <RequiredText />
              <Select2
                  v-if="forUser"
                  :parent-id="`#${id}`"
                  containerClass="col-12 mt-auto mb-5"
                  :name="`${id}-perimeter-users`"
                  :multiple="true"
                  :label="$t('access.label.users')"
                  labelClass="d-flex align-items-center fs-6 fw-bold mb-2"
                  selectClass="form-select form-select-solid"
                  :isRequired="true"
                  :placeholderText="$t('access.placeholder.users')"
                  v-model="formData.ids"
                  :IsInputGroup="true"
                  icon="bi bi-plus"
                  :clickAddon="openCreateUserModal"
              >
                <option v-for="(user, index) in filteredUsers" :key="id+index" :value="user.id">
                  {{ user.fullname }}
                </option>
              </Select2>
              <Select2
                  v-else
                  :parent-id="`#${id}`"
                  containerClass="col-12 mt-auto mb-5"
                  :name="`${id}-perimeter-groups`"
                  :multiple="true"
                  :label="$t('access.label.groups')"
                  labelClass="d-flex align-items-center fs-6 fw-bold mb-2"
                  selectClass="form-select form-select-solid"
                  :isRequired="true"
                  :placeholderText="$t('access.placeholder.groups')"
                  v-model="formData.ids"
                  :IsInputGroup="true"
                  icon="bi bi-plus"
                  :clickAddon="openCreateGroupModal"
              >
                <option v-for="(group, index) in filteredGroup" :key="id+index" :value="group.id">
                  {{ group.name }}
                </option>
              </Select2>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.close" @click="closeModal"></Button>
            <Button btn-class="btn-sm" type="submit" id="submit_btn" btn-text="button.save" size="7px 30px" :is-loading="isLoading"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
  <UserGroupFormModal :id="'createUserGroupModal'+id" :parent="id" @callback="afterCreateNewGroupOrUser"/>
  <UserFormModal :id="'createUserModal'+id" :parent="id" @afterCreate="afterCreateNewGroupOrUser" />
</template>

<script setup>
import { computed, defineProps, onMounted, ref, watch } from 'vue'
import {useStore} from "vuex";
import RequiredText from "@/components/RequiredText"
import Button from "@/components/Button.vue"
import {useLocales} from "@/composables/locales";
import Select2 from "@/components/Select2.vue";
import UserGroupFormModal from "@/components/UserGroup/UserGroupFormModal.vue";
import UserFormModal from "@/components/modals/UserForm.vue";

const { locale } = useLocales()

const store = useStore()

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  isDoor: {
    type: Boolean,
    required: true
  },
  perimeter: {
    type: String,
    default: '',
    required: true
  },
  forUser: {
    type: Boolean,
    default: true
  },
  parent: {
    type: String,
    required: false
  },
  oldData: {
    type: Array,
    default: []
  }
})

const emits = defineEmits(['callback'])

const formData = ref({
  ids: []
})
const isLoading = ref(false)

const currentUser = computed(() => store.getters['user/currentUser'])

const allUsers = computed(() => store.getters['user/getUsers'])
const officeUsers = computed(() => {
  if (!allUsers.value?.length) return []
  return allUsers.value.filter(user => user.office_id === currentUser.value.office_id)
})

const filteredUsers = computed(() => {
  if (!props.oldData?.length) return allUsers.value
  return allUsers.value.filter(user => !props.oldData.includes(user.id))
})

const groups = computed(() => store.getters['usersGroups/getUsersGroups'])
const filteredGroup = computed(() => {
  if (!props.oldData?.length) return groups.value
  return groups.value.filter(group => !props.oldData.includes(group.id))
})


function closeModal(){
  if(props.parent){
    $('#'+props.parent).modal('toggle')
  }
  $('#'+props.id).modal('toggle')
  formData.value.ids = null
  formData.value.ids = []
}

function openCreateGroupModal() {
  $('#'+props.id).modal('toggle')
  $('#createUserGroupModal'+props.id).modal('toggle')
}

function openCreateUserModal() {
  $('#'+props.id).modal('toggle')
  $('#createUserModal'+props.id).modal('toggle')
}

function afterCreateNewGroupOrUser(id) {
  formData.value.ids.push(id)
}



function submitForm() {
  isLoading.value = true

  let data = {
    door: props.isDoor ? props.perimeter : null,
    perimeter: !props.isDoor ? props.perimeter : null,
    users: props.forUser ? formData.value.ids : null,
    groups: !props.forUser ? formData.value.ids: null
  }

  const notif = props.forUser ? 'notification.user_added' : 'notification.group_added'

  store.dispatch('doors/addUsersToDoorOrPerimeter', data)
      .then((response) => {
        emits('callback')
        closeModal()
        window.toastr['success'](i18n.global.t(notif))
      })
      .catch()
      .finally(() => isLoading.value = false)
}

function loadData() {
  store.dispatch('user/loadUsers')
  store.dispatch('usersGroups/loadUsersGroups')
}

onMounted(() => {
  loadData()
})



</script>